import PropTypes from 'prop-types'
import R from 'ramda'
import { Route, Switch, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useMedia } from 'use-media'

import { Box, Button, Icon, HR, mediaQueries } from '@redriverpress/system'

import CategoryChip from '../../../common/components/CategoryChip'
import CategoryHeader from './CategoryHeader'
import Flashcard from '../Flashcard'
import FlashcardsGrid from '../FlashcardsGrid'
import FlashcardsSkeleton from '../skeletons/FlashcardsSkeleton'
import GenreHeader from './GenreHeader'
import useCategory from '../../hooks/useCategory'
import useGenre from '../../hooks/useGenre'
import useGenres from '../../hooks/useGenres'
import { GENRE_AND_CATEGORY_PATH, GENRE_PATH } from '../../constants/routes'

const genreChunk = 36

export default function Genre({ userHasAccess, userLoggedIn }) {
  const isMd = useMedia(mediaQueries.md)
  const { genreSlug } = useParams()
  const { activeGenre } = useGenres()
  const { isLoading: isLoadingGenre, genre, activeCategory } = useGenre(
    activeGenre?.id
  )
  const { category } = useCategory(activeCategory?.id)

  // Some genres contain hundreds of flashcards; categories are usually < 100.
  // When showing all flashcards in a genre, only show 36 flashcards at a time,
  // to prevent performance slowdowns from React having to deal with hundreds of
  // flashcards (and their modals) all at once.
  const [genreAmount, setGenreAmount] = useState(genreChunk)

  const incrementGenreAmount = () =>
    setGenreAmount(amount => amount + genreChunk)

  const chunkedGenreFlashcards = genre?.flashcards
    ? R.take(genreAmount, genre?.flashcards)
    : []

  const sectionRef = useRef()

  const scrollToSectionRef = () => {
    const navHeight = document.querySelector('[data-esll-navbar').offsetHeight

    // Scroll to top of genre when switching genres
    window.scrollTo({
      top: sectionRef?.current?.offsetTop - navHeight,
      left: 0,
      behavior: 'smooth',
    })
  }

  useEffect(() => {
    isMd && scrollToSectionRef()

    // Reset genreAmount when switching genres
    setGenreAmount(genreChunk)
  }, [genreSlug])

  return (
    <Box as='section' ref={sectionRef}>
      {/* On default: genre header should only render when a category isn't selected. */}
      {/* On md+: genre header should always be rendered. */}
      {/* This is managed by leveraging the `exact` prop of the Route with the state of the media query. */}
      <Route path={GENRE_PATH} exact={!isMd}>
        <GenreHeader />
      </Route>

      {isLoadingGenre || !genre ? (
        <FlashcardsSkeleton />
      ) : (
        <Box>
          {isMd && (
            <>
              <Box
                as='nav'
                aria-label='Categories'
                display='flex'
                flexWrap='wrap'
                m={-1}
              >
                {genre.flashcardCategories.map(cat => (
                  <CategoryChip
                    key={cat.id}
                    to={`/flashcards/${genreSlug}/${cat.slug}`}
                    exact={cat.slug === ''}
                  >
                    {cat.title} ({cat.flashcardsCount})
                  </CategoryChip>
                ))}
              </Box>
              <HR my={4} borderColor='gray200' />
            </>
          )}

          <Switch>
            <Route path={GENRE_AND_CATEGORY_PATH}>
              <CategoryHeader
                userHasAccess={userHasAccess}
                userLoggedIn={userLoggedIn}
              />
              {category && (
                <FlashcardsGrid>
                  {category.flashcards.map(fc => (
                    <Flashcard
                      key={fc.id}
                      flashcard={fc}
                      userHasAccess={userHasAccess}
                    />
                  ))}
                </FlashcardsGrid>
              )}
            </Route>

            <Route path={GENRE_PATH} exact>
              <FlashcardsGrid>
                {chunkedGenreFlashcards.map(fc => (
                  <Flashcard
                    key={fc.id}
                    flashcard={fc}
                    userHasAccess={userHasAccess}
                  />
                ))}
              </FlashcardsGrid>

              {genreAmount < genre?.flashcards?.length && (
                <Box textAlign='center' my={4}>
                  <Button onClick={incrementGenreAmount}>
                    View More
                    <Icon icon='arrow-down' pl={2} />
                  </Button>
                </Box>
              )}
            </Route>
          </Switch>
        </Box>
      )}
    </Box>
  )
}

Genre.propTypes = {
  userHasAccess: PropTypes.bool.isRequired,
  userLoggedIn: PropTypes.bool.isRequired,
}
